/*
 * AI credits (crud/ai_credits.py): every AI action costs 1 credit, charged to
 * whoever clicks. 100 for the first month (from first use), then 25 a month,
 * each coming back 30 days after it was spent. Admins can add extra credits (25 by default) for 28 days
 * and set allowances per role / per user, each with a reason.
 */
export type AiCreditPhase = 'first_month' | 'monthly';

export const AI_CREDIT_FEATURE_LABELS: Record<string, string> = {
  profile_generation: 'Profile generation',
  project_writeup: 'Project write-up draft',
  cv_import: 'CV import',
};

// Fired after any AI call (spent or refused) so balances refresh.
export const AI_CREDITS_CHANGED_EVENT = 'ai-credits-changed';

export interface AiCreditGrant {
  sk: string;
  amount: number;
  spent: number;
  remaining: number;
  expires_at: string;
  created_at?: string;
  reason?: string;
  granted_by?: string;
}

export interface AiCreditHistoryRow {
  kind: 'spend' | 'grant';
  created_at: string;
  feature?: string;
  feature_label?: string;
  person_id?: string | null;
  person_name?: string | null;
  source?: 'intro' | 'allowance' | 'grant' | 'exempt';
  status?: 'charged' | 'refunded';
  refund_reason?: string;
  amount?: number;
  expires_at?: string;
  reason?: string;
  granted_by?: string;
}

export interface AiCreditBalance {
  user: string;
  exempt: boolean;
  available: number;
  allowance: number;
  allowance_used: number;
  allowance_left: number;
  monthly_allowance: number;
  allowance_source: string; // 'default' | 'override' | 'role:<name>'
  phase: AiCreditPhase;
  first_use_at: string | null;
  first_month_ends_at: string | null;
  next_credit_back_at: string | null;
  grants: AiCreditGrant[];
  grant_left: number;
  history?: AiCreditHistoryRow[];
}

export interface AiCreditUserRow extends AiCreditBalance {
  roles: string[];
  person_id?: string | null;
  person_name?: string | null;
  spent_last_30_days: number;
  uses_total: number;
  last_used_at?: string | null;
  override_reason?: string | null;
}

export interface AiCreditRoleRow {
  role: string;
  monthly_allowance: number | null;
  reason?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface AiCreditAuditRow {
  action: string;
  target_type: 'user' | 'role';
  target: string;
  value: number | null;
  reason: string;
  by: string;
  created_at: string;
}

export interface AiCreditsOverview {
  users: AiCreditUserRow[];
  roles: AiCreditRoleRow[];
  audit: AiCreditAuditRow[];
  defaults: {
    first_month: number;
    first_month_days: number;
    monthly: number;
    window_days: number;
    grant_amount: number; // the default grant
    max_grant_amount?: number;
    grant_days: number;
    max_allowance: number;
  };
}

export const allowanceSourceLabel = (source: string) => {
  if (source === 'override') return 'set for this person';
  if (source.startsWith('role:')) return `from the ${source.slice(5)} role`;
  return 'standard';
};
